// ============================================
// Search.js
// 特定病院一覧の検索・無限スクロール
// ============================================

const CONFIG = {
  rootSelector: '[data-hospital-app]',
  listSelector: '[data-hospital-list]',
  countSelector: '[data-result-count]',
  inputSelector: '[data-search-input]',
  sentinelSelector: '[data-load-more-sentinel]',
  inputDelay: 160,
  batchSize: 50,
};

const ICONS = {
  location: `<svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M8.78906 0C5.2748 0 2.34375 2.83086 2.34375 6.44531C2.34375 7.82039 2.75695 9.04657 3.54996 10.1955L8.29582 17.601C8.52605 17.961 9.05254 17.9603 9.28231 17.601L14.0487 10.1704C14.8247 9.07344 15.2344 7.78543 15.2344 6.44531C15.2344 2.89137 12.343 0 8.78906 0ZM8.78906 9.375C7.17371 9.375 5.85938 8.06067 5.85938 6.44531C5.85938 4.82996 7.17371 3.51563 8.78906 3.51563C10.4044 3.51563 11.7188 4.82996 11.7188 6.44531C11.7188 8.06067 10.4044 9.375 8.78906 9.375Z" fill="#00479D" />
    <path d="M13.3697 13.4647L10.4193 18.0774C9.65555 19.2681 7.91832 19.2642 7.15828 18.0786L4.20305 13.4659C1.60289 14.067 0 15.1683 0 16.4844C0 18.7681 4.52844 20 8.78906 20C13.0497 20 17.5781 18.7681 17.5781 16.4844C17.5781 15.1674 15.973 14.0655 13.3697 13.4647Z" fill="#00479D" />
  </svg>`,
  phone: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M17.07 2.92787C13.1642 -0.976782 6.83251 -0.975845 2.92787 2.93001C-0.976782 6.83587 -0.975845 13.1675 2.93001 17.0722C6.83587 20.9768 13.1675 20.9759 17.0722 17.07C18.9474 15.1942 20.0006 12.6502 20 9.99787C19.9995 7.34591 18.9455 4.80279 17.07 2.92787ZM15.1447 13.9299C15.1442 13.9303 15.1438 13.9308 15.1433 13.9312V13.9279L14.6367 14.4312C13.9814 15.0948 13.027 15.3678 12.12 15.1512C11.2061 14.9066 10.3374 14.5172 9.54666 13.9979C8.81205 13.5284 8.13126 12.9796 7.51666 12.3612C6.95115 11.7999 6.4431 11.1835 5.99998 10.5212C5.51529 9.80865 5.13166 9.0324 4.85998 8.21455C4.54853 7.25377 4.80662 6.19951 5.52666 5.49123L6.11998 4.89791C6.28494 4.7322 6.55298 4.73162 6.71865 4.89658C6.71908 4.89701 6.71955 4.89744 6.71998 4.89791L8.5933 6.77123C8.759 6.93619 8.75959 7.20423 8.59462 7.3699C8.59419 7.37033 8.59377 7.37076 8.5933 7.37123L7.4933 8.47123C7.17767 8.78341 7.13798 9.27951 7.39998 9.63791C7.79783 10.1839 8.2381 10.6977 8.71666 11.1746C9.25021 11.7104 9.83025 12.1979 10.45 12.6313C10.8081 12.881 11.2935 12.8389 11.6033 12.5313L12.6666 11.4513C12.8316 11.2856 13.0996 11.285 13.2653 11.4499C13.2657 11.4504 13.2661 11.4508 13.2666 11.4513L15.1433 13.3313C15.309 13.4962 15.3096 13.7642 15.1447 13.9299Z" fill="white" />
  </svg>`,
};

function hospitalSearch() {
  const root = document.querySelector(CONFIG.rootSelector);
  if (!root) return;

  const list = root.querySelector(CONFIG.listSelector);
  const count = root.querySelector(CONFIG.countSelector);
  const input = root.querySelector(CONFIG.inputSelector);
  const sentinel = root.querySelector(CONFIG.sentinelSelector);
  const dataUrl = root.dataset.dataUrl;

  if (!list || !count || !input || !sentinel || !dataUrl) return;

  const initialMarkup = list.innerHTML;
  const initialRenderedCount = list.querySelectorAll('[data-hospital-card]').length;

  let allItems = null;
  let dataPromise = null;
  let filtered = [];
  let renderedCount = initialRenderedCount;
  let timer = 0;
  let observer = null;
  let activeQuery = '';

  function setupObserver() {
    if (observer) observer.disconnect();
    observer = new IntersectionObserver(
      async (entries) => {
        if (!entries[0]?.isIntersecting) return;

        try {
          await ensureData();
          if (activeQuery === '') {
            filtered = allItems;
          }
          appendNextBatch();
        } catch {
          renderError();
        }
      },
      { rootMargin: '200% 0px' }
    );
    observer.observe(sentinel);
  }

  function appendNextBatch() {
    if (renderedCount >= filtered.length) {
      observer?.disconnect();
      return;
    }

    const batch = filtered.slice(renderedCount, renderedCount + CONFIG.batchSize);
    const fragment = document.createDocumentFragment();
    for (const item of batch) {
      const tmp = document.createElement('template');
      tmp.innerHTML = itemHtml(item);
      fragment.appendChild(tmp.content.firstChild);
    }
    list.appendChild(fragment);
    renderedCount += batch.length;

    if (renderedCount >= filtered.length) {
      observer?.disconnect();
    }
  }

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(async () => {
      try {
        await ensureData();
        applySearch(input.value);
      } catch {
        renderError();
      }
    }, CONFIG.inputDelay);
  });

  async function ensureData() {
    if (allItems !== null) return;
    if (dataPromise !== null) return dataPromise;

    dataPromise = fetch(dataUrl, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((items) => {
        allItems = Array.isArray(items) ? items : [];
      })
      .finally(() => { dataPromise = null; });

    return dataPromise;
  }

  function applySearch(value) {
    const query = normalize(value);
    activeQuery = query;

    if (query === '') {
      // 検索クリア時はPHPの初期表示に戻し、続きだけJSONから追加する。
      filtered = allItems;
      count.textContent = `${filtered.length.toLocaleString()}件`;
      list.innerHTML = initialMarkup;
      renderedCount = initialRenderedCount;
      setupObserver();
      return;
    }

    filtered = allItems.filter((item) => String(item.search || '').includes(query));
    count.textContent = `${filtered.length.toLocaleString()}件`;

    if (filtered.length === 0) {
      list.innerHTML = '<li class="p-hospital-empty">該当する病院が見つかりませんでした。</li>';
      observer?.disconnect();
      return;
    }

    list.innerHTML = '';
    renderedCount = 0;
    appendNextBatch();
    setupObserver();
  }

  function renderError() {
    count.textContent = '0件';
    list.innerHTML = '<li class="p-hospital-empty">病院データを読み込めませんでした。</li>';
    observer?.disconnect();
  }

  function itemHtml(item) {
    const phone = item.phone || '';
    return `<li class="p-hospital-card" data-hospital-card>
      <h3 class="p-hospital-card__name">${escapeHtml(item.name || '')}</h3>
      <div class="p-hospital-card__body">
        <div class="p-hospital-card__info">
          <p class="p-hospital-card__address">
            <span aria-hidden="true">${ICONS.location}</span>
            <span class="-zip">〒${escapeHtml(item.zip || '')}</span>
            <span class="-address">${escapeHtml(item.address || '')}</span>
          </p>
          ${phone ? `<p>
            <a class="p-hospital-card__tel" href="tel:${escapeHtml(telHref(phone))}">
              <span aria-hidden="true">${ICONS.phone}</span>
              ${escapeHtml(phone)}
            </a>
          </p>` : ''}
        </div>
        <dl class="p-hospital-card__category">
          <dt>区分名称</dt>
          <dd>${escapeHtml(item.category || '')}</dd>
        </dl>
      </div>
    </li>`;
  }

  setupObserver();
}

function normalize(value) {
  return String(value || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[ぁ-ん]/g, (char) => String.fromCharCode(char.charCodeAt(0) + 0x60))
    .replace(/\s+/g, '');
}

function telHref(value) {
  return String(value).replace(/[^0-9+]/g, '') || value;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export { hospitalSearch };
